/**
 * Web Component entry point for the Campaign Detail widget.
 *
 * Importing this module registers the <legion-campaign-detail> custom element
 * so it can be used in any HTML page or framework that supports web components.
 *
 * @example
 * ```ts
 * import '@legionhandtech/lht-react-widgets/campaign-detail-web-component';
 *
 * // Then use in HTML:
 * // <legion-campaign-detail id="uuid" api-key="token" base-url="https://..." />
 * ```
 */

import { CAMPAIGN_DETAIL_THEMING_PROP_TO_PARAM } from "./types";
import {
  attachAuthBridge,
  type AuthBridgeHandle,
  AUTH_BRIDGE_OBSERVED_ATTRS,
} from "../shared/auth-bridge";

const DETAIL_CSS_ATTRIBUTES = Object.values(
  CAMPAIGN_DETAIL_THEMING_PROP_TO_PARAM
);
const TOKEN_ATTRS = new Set<string>(AUTH_BRIDGE_OBSERVED_ATTRS);

class LegionCampaignDetailElement extends HTMLElement {
  public iframe: HTMLIFrameElement | null = null;
  private authBridge: AuthBridgeHandle | null = null;
  private boundMessageHandler: ((e: MessageEvent) => void) | null = null;

  constructor() {
    super();
    this.attachShadow({ mode: "open" });
  }

  static get observedAttributes(): string[] {
    return [
      "id",
      "theme",
      "base-url",
      "height",
      "width",
      ...AUTH_BRIDGE_OBSERVED_ATTRS,
      ...DETAIL_CSS_ATTRIBUTES,
    ];
  }

  connectedCallback(): void {
    this.authBridge = attachAuthBridge(this, {
      iframeAccessor: () => this.iframe,
      baseUrl: this.baseUrl,
    });
    this.boundMessageHandler = this.handleIframeMessage.bind(this);
    window.addEventListener("message", this.boundMessageHandler);
    this.render();
  }

  disconnectedCallback(): void {
    this.authBridge?.detach();
    this.authBridge = null;
    if (this.boundMessageHandler) {
      window.removeEventListener("message", this.boundMessageHandler);
      this.boundMessageHandler = null;
    }
  }

  attributeChangedCallback(
    name: string,
    oldValue: string | null,
    newValue: string | null
  ): void {
    if (oldValue === newValue) return;
    // Token changes don't require a full rebuild — push via postMessage.
    if (TOKEN_ATTRS.has(name) && this.iframe) {
      this.authBridge?.postCurrentToken();
      return;
    }
    this.render();
  }

  private get campaignId(): string | null {
    return this.getAttribute("id");
  }

  private get apiKey(): string | null {
    return this.authBridge?.currentToken() ?? null;
  }

  private get theme(): string {
    return this.getAttribute("theme") || "light";
  }

  private get baseUrl(): string {
    return this.getAttribute("base-url") || window.location.origin;
  }

  private get widgetHeight(): string {
    return this.getAttribute("height") || "auto";
  }

  private get widgetWidth(): string {
    return this.getAttribute("width") || "100%";
  }

  private handleIframeMessage(event: MessageEvent): void {
    let expectedOrigin: string | null = null;
    try {
      expectedOrigin = new URL(this.baseUrl).origin;
    } catch {
      expectedOrigin = null;
    }
    // Reject all messages if expectedOrigin could not be derived from
    // base-url, otherwise a forged event from any page could trigger
    // close / claim / requestlogin CustomEvents on the host.
    if (!expectedOrigin || event.origin !== expectedOrigin) return;
    const data = event.data;
    if (!data || typeof data !== "object") return;
    if (data.type === "CAMPAIGN_DETAIL_CLOSE") {
      this.dispatchEvent(new CustomEvent("close"));
    } else if (data.type === "CAMPAIGN_DETAIL_CLAIMED" && data.id) {
      this.dispatchEvent(
        new CustomEvent("claim", { detail: { id: String(data.id) } })
      );
    } else if (data.type === "CAMPAIGN_DETAIL_REQUEST_LOGIN") {
      this.dispatchEvent(
        new CustomEvent("requestlogin", {
          detail: { id: data.id ? String(data.id) : null },
        })
      );
    }
  }

  render(): void {
    if (!this.shadowRoot) return;

    if (!this.campaignId) {
      this.shadowRoot.innerHTML = `<div style="padding:32px;text-align:center;color:#dc3545;background:#f8f9fa;border:1px solid #dee2e6;border-radius:8px;">Campaign ID is required</div>`;
      return;
    }

    const iframe = document.createElement("iframe");
    const url = new URL(
      `/widget/campaign-detail/${encodeURIComponent(this.campaignId)}`,
      this.baseUrl
    );
    url.searchParams.set("theme", this.theme);

    if (this.apiKey) {
      url.searchParams.set("apiKey", this.apiKey);
    }

    DETAIL_CSS_ATTRIBUTES.forEach((attr) => {
      const val = this.getAttribute(attr);
      if (val !== null && val !== undefined && String(val).length > 0) {
        url.searchParams.set(attr, val);
      }
    });

    iframe.src = url.toString();
    iframe.width = "100%";
    iframe.frameBorder = "0";
    iframe.scrolling = "auto";
    iframe.title = "Campaign Detail Widget";

    const isFullHeight =
      this.widgetHeight === "auto" || this.widgetHeight === "100%";
    if (!isFullHeight) {
      iframe.height = this.widgetHeight.replace("px", "");
    }

    iframe.onload = () => {
      this.authBridge?.postCurrentToken();
      this.dispatchEvent(
        new CustomEvent("load", {
          detail: {
            widget: "campaign-detail",
            id: this.campaignId,
            theme: this.theme,
          },
        })
      );
    };

    iframe.onerror = () => {
      if (!this.shadowRoot) return;
      this.shadowRoot.innerHTML = `<div style="padding:32px;text-align:center;color:#856404;background:#fff3cd;border:1px solid #ffeaa7;border-radius:8px;">Failed to load campaign detail widget</div>`;
    };

    const widthCss =
      this.widgetWidth.includes("%") ?
        this.widgetWidth
      : this.widgetWidth + "px";

    const style = document.createElement("style");
    style.textContent = `
      :host {
        display: block;
        width: ${widthCss};
        ${isFullHeight ? "height: 100%; min-height: 480px;" : `height: ${this.widgetHeight};`}
      }
      iframe {
        width: 100%;
        height: 100%;
        ${isFullHeight ? "min-height: 480px;" : ""}
        border: none;
        border-radius: 8px;
        background: ${this.theme === "dark" ? "#1a1a1a" : "white"};
      }
    `;

    this.shadowRoot.innerHTML = "";
    this.shadowRoot.appendChild(style);
    this.shadowRoot.appendChild(iframe);
    this.iframe = iframe;
  }
}

if (
  typeof customElements !== "undefined" &&
  !customElements.get("legion-campaign-detail")
) {
  customElements.define("legion-campaign-detail", LegionCampaignDetailElement);
}

export { LegionCampaignDetailElement };
